import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js'

/**
 * Extract all text content from a PDF ArrayBuffer.
 * Returns the full text as a single string (one line per page).
 */
export async function extractTextFromPdf(arrayBuffer) {
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  let fullText = ''

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const content = await page.getTextContent()
    const strings = content.items.map((item) => item.str)
    fullText += strings.join(' ') + '\n'
  }

  return fullText
}

/**
 * Parse IKEA planner PDF text into cabinet groups with filtered items.
 *
 * PDF format per page (one continuous line):
 *   PAGE (TOTAL)  NUM. CODE - Description  (pagination)  TITLE  desc  £price  article  qty x  £total  ...
 *
 * Item format:
 *   VOXTORP  drawer front, matt white, 60x40 cm  £30.00  702.731.91  1 x   £30.00
 */
/**
 * Detect the region of an IKEA planner PDF based on currency and dimension units.
 * Returns 'uk' for £/cm, 'us' for $/", or null if indeterminate.
 */
export function detectPdfRegion(fullText) {
  const hasPound = /£/.test(fullText)
  const hasDollar = /\$/.test(fullText)
  const hasCm = /\d+\s*x\s*\d+\s*cm/i.test(fullText)
  const hasInch = /\d+\s*x\s*\d+\s*"/.test(fullText)

  if (hasPound && hasCm) return 'uk'
  if (hasDollar && hasInch) return 'us'
  if (hasPound) return 'uk'
  if (hasDollar) return 'us'
  if (hasCm) return 'uk'
  if (hasInch) return 'us'
  return null
}

export function parseIkeaPlannerText(fullText) {
  // Step 1: Extract all items with their article numbers across the full text
  const allItems = extractAllItems(fullText)

  // Step 2: Extract all cabinet headers across the full text
  const cabinetHeaders = extractCabinetHeaders(fullText)

  // Step 3: Group items by cabinet using text position
  const groups = groupItemsByCabinet(cabinetHeaders, allItems)

  // Step 4: Filter to only doors/drawer fronts and classify
  for (const group of groups) {
    const filtered = group.items.filter((item) => {
      const type = item.type.toLowerCase()
      return type.includes('drawer front') || type === 'door'
    })
    group.items = classifyItems(filtered)
  }

  return groups.filter((g) => g.items.length > 0)
}

/**
 * Extract all product items from the full text.
 * Uses IKEA article numbers (XXX.XXX.XX) as anchors.
 *
 * Handles two PDF formats:
 *   Format A: TITLE  desc  £price  XXX.XXX.XX  qty x  £total
 *   Format B: TITLE  desc  XXX.XXX.XX  qty x
 */
function extractAllItems(fullText) {
  const items = []

  const itemRegex =
    /([A-ZÅÄÖ][A-ZÅÄÖ0-9/\s]*?)\s+([a-zåäö](?:(?!\d{3}\.\d{3}\.\d{2}).)*?)\s+(?:[£$€]([\d,.]+)\s+)?(\d{3}\.\d{3}\.\d{2})\s+(\d+)\s*x(?:\s+[£$€]([\d,.]+))?/g

  let match
  while ((match = itemRegex.exec(fullText)) !== null) {
    const title = match[1].trim()
    const description = match[2].trim()
    const price = match[3] || null
    const articleNum = match[4]
    const quantity = parseInt(match[5])
    const textPosition = match.index

    // Parse description parts (comma-separated)
    const descParts = description.split(',').map((p) => p.trim())

    // First part is the type (e.g., "drawer front", "door", "base cabinet frame")
    const type = descParts[0] || ''

    // Look for position (low, medium, high) in description parts
    let position = null
    for (const part of descParts) {
      const lower = part.toLowerCase().trim()
      if (['low', 'medium', 'high'].includes(lower)) {
        position = lower
        break
      }
    }

    // Look for HxW dimension (e.g., "60x40 cm", '15x24"')
    const dimension = extractDimension(description)

    // Look for standalone width (e.g., "60 cm" without HxW)
    const standaloneWidth = !dimension ? extractStandaloneWidth(description) : null

    items.push({
      title,
      description,
      type,
      position,
      dimension,
      standaloneWidth,
      articleNum,
      price,
      quantity,
      textPosition
    })
  }

  return items
}

/**
 * Extract cabinet headers from text.
 * Format: "13. ME/MA 145 - Base cabinet with drawers and inner drawers"
 * Also handles: "16. ME 306 - Wall cabinet with shelves"
 */
function extractCabinetHeaders(fullText) {
  const headers = []

  // Match: number. CODE - description
  // Code can contain letters, numbers, /, spaces, and optional *
  const headerRegex =
    /(\d+)\.\s+([A-Z][A-Z0-9/\s]*?\*?)\s+-\s+([\w\s]+?)(?=\s{2,}|\s+\(|\s+[A-ZÅÄÖ]{3,}\s+[a-z]|\s+This\s)/g

  let match
  while ((match = headerRegex.exec(fullText)) !== null) {
    headers.push({
      itemNum: parseInt(match[1]),
      code: match[2].trim(),
      description: match[3].trim(),
      textPosition: match.index
    })
  }

  // Deduplicate headers (same cabinet can appear on multiple pages)
  const seen = new Set()
  return headers.filter((h) => {
    const key = h.itemNum + '.' + h.code
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * Group items by their nearest preceding cabinet header.
 */
function groupItemsByCabinet(headers, items) {
  if (headers.length === 0) {
    // No headers found — put all items in a single unnamed group
    return [
      {
        itemNum: 0,
        code: '',
        description: 'Items',
        items: items
      }
    ]
  }

  // For each item, find which cabinet it belongs to based on text position
  const groups = headers.map((h) => ({
    itemNum: h.itemNum,
    code: h.code,
    description: h.description,
    items: []
  }))

  for (const item of items) {
    // Find the last header that appears before this item in the text
    let bestIdx = -1
    for (let i = 0; i < headers.length; i++) {
      if (headers[i].textPosition <= item.textPosition) {
        bestIdx = i
      }
    }
    if (bestIdx >= 0) {
      groups[bestIdx].items.push(item)
    }
  }

  return groups
}

/**
 * Classify filtered items (doors and drawer fronts) into final output format.
 *
 * - Has HxW dimension → door or drawer front, extract height & width in mm
 * - No HxW, is drawer front with position + width → int-drw
 * - Otherwise → include with available info
 */
function classifyItems(items) {
  return items.map((item, index) => {
    const isDoor = item.type.toLowerCase() === 'door'
    const isDrawerFront = item.type.toLowerCase().includes('drawer front')
    const hasHxW = item.dimension !== null

    if (hasHxW) {
      return {
        id: index,
        selected: true,
        ikeaTitle: item.title,
        type: isDoor ? 'door' : 'drawer front',
        position: null,
        heightMm: convertToMm(item.dimension.height, item.dimension.unit),
        widthMm: convertToMm(item.dimension.width, item.dimension.unit),
        quantity: item.quantity,
        hingeSide: null,
        originalUnit: item.dimension.unit
      }
    } else if (isDrawerFront && item.position && item.standaloneWidth) {
      // Internal drawer: drawer front with position + standalone width, no HxW
      return {
        id: index,
        selected: true,
        ikeaTitle: item.title,
        type: 'int-drw',
        position: item.position,
        heightMm: null,
        widthMm: convertToMm(item.standaloneWidth.value, item.standaloneWidth.unit),
        quantity: item.quantity,
        hingeSide: null,
        originalUnit: item.standaloneWidth.unit
      }
    } else {
      return {
        id: index,
        selected: true,
        ikeaTitle: item.title,
        type: isDoor ? 'door' : isDrawerFront ? 'drawer front' : item.type,
        position: item.position,
        heightMm: null,
        widthMm: item.standaloneWidth ? convertToMm(item.standaloneWidth.value, item.standaloneWidth.unit) : null,
        quantity: item.quantity,
        hingeSide: null,
        originalUnit: item.standaloneWidth ? item.standaloneWidth.unit : null
      }
    }
  })
}

/**
 * Extract HxW dimension from description string.
 * Matches: "60x40 cm", "60x40cm", '15x24"', "15 x 24"", etc.
 * Only matches 2-number format (HxW), not 3-number (WxDxH cabinet dimensions).
 */
function extractDimension(str) {
  // Strip any 3D dimensions (WxDxH like "60x60x80 cm") before searching for 2D
  const cleaned = str.replace(/\d+(?:\.\d+)?\s*x\s*\d+(?:\.\d+)?\s*x\s*\d+(?:\.\d+)?\s*(?:cm|")/gi, '')

  // Match WxH: "60x40 cm", "60x40cm", '15x24"'
  const dimMatch = cleaned.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*(cm|")/i)
  if (dimMatch) {
    return {
      width: parseFloat(dimMatch[1]),
      height: parseFloat(dimMatch[2]),
      unit: dimMatch[3]
    }
  }
  return null
}

/**
 * Extract standalone width from description (not in HxW format).
 * E.g., "60 cm" at the end of "drawer front, low, white, 60 cm"
 */
function extractStandaloneWidth(str) {
  const match = str.match(/(?:,\s*|\s)(\d+(?:\.\d+)?)\s*(cm|")(?:\s|$)/i)
  if (match) {
    // Make sure this isn't part of an HxW pattern
    const idx = str.indexOf(match[0])
    const before = str.substring(Math.max(0, idx - 5), idx)
    if (/\d\s*x\s*$/.test(before)) return null

    return { value: parseFloat(match[1]), unit: match[2] }
  }
  return null
}

/**
 * Convert value to mm.
 * cm → ×10, inches (") → ×25.4
 */
function convertToMm(value, unit) {
  if (unit === 'cm') return Math.round(value * 10)
  if (unit === '"') return Math.round(value * 25.4)
  return value
}
